/**
 * V5.1 — Asset-Subtype-Logik.
 *
 * Pure Function. Leitet aus Symbol, Asset-Klasse, Watchlist-Rolle und Theme-Tags
 * den feineren `asset_subtype` ab, der die Score-Schwerpunkte bestimmt.
 *
 * Genutzt von:
 * - Watchlist-Migration (V5.1.S4) zur Erst-Befüllung
 * - Score-Engine V5.1 zur Wahl der richtigen Strategie-Bewertung
 */

import type { AssetClass, AssetSubtype, ThemeTag, WatchlistRole } from '@/types';

interface InferAssetSubtypeInput {
  symbol: string;
  asset_class: AssetClass;
  watchlist_role?: WatchlistRole | null;
  theme_tags?: ThemeTag[];
  /** Aktien-Subtype-Hinweis aus Watchlist-Metadaten (wird später durch Fundamentals ersetzt) */
  is_dividend_aristocrat?: boolean;
  is_quality_value?: boolean;
}

const TESLA_SYMBOLS = new Set(['TSLA']);
const QUALITY_HINT_SYMBOLS = new Set([
  // Burggraben + ROIC>10% + Margenstabilität (manuell kuratiert, nicht abschließend)
  'AAPL', 'BRK.B', 'COST', 'DE', 'HD', 'ICE', 'JNJ', 'JPM', 'LIN', 'LMT',
  'MA', 'MCO', 'MSCI', 'MSFT', 'SPGI', 'UNH', 'V', 'WM', 'WMT',
]);
const DIVIDEND_ARISTOCRAT_SYMBOLS = new Set([
  // Klassische Aristokraten ≥10 Jahre Steigerung (manuell kuratiert)
  'ABBV', 'CL', 'CVX', 'KMB', 'KO', 'MCD', 'MMM', 'O', 'PG', 'XOM',
]);
const INDEX_ETF_SYMBOLS = new Set(['SPY', 'QQQ', 'IWM', 'VT', 'VOO', 'VTI']);

export function inferAssetSubtype(input: InferAssetSubtypeInput): AssetSubtype | null {
  const sym = input.symbol.toUpperCase();

  // 1. Tesla bekommt eigenen Subtype (User-Direktive — High-Beta-Spezial)
  if (TESLA_SYMBOLS.has(sym)) return 'tesla';

  // 2. Asset-Klasse-spezifische Defaults
  if (input.asset_class === 'commodity_etf') return 'gold';
  if (input.asset_class === 'crypto') return 'crypto_anchor';

  if (input.asset_class === 'etf') {
    if (INDEX_ETF_SYMBOLS.has(sym)) return 'index_etf';
    return 'sector_etf';
  }

  // 3. Stocks — feinere Logik via Tags + manueller Liste
  if (input.asset_class === 'stock') {
    // Theme-dominiert?
    if (input.theme_tags && input.theme_tags.length > 0) {
      // Aristokrat-Tag schlägt Theme (z.B. Aktie ist Aristokrat UND Theme-Wert)
      if (input.theme_tags.includes('theme_dividend_aristocrat')) return 'dividend';
      // sonst: Themenwert
      if (
        input.theme_tags.some((t) =>
          t === 'theme_ai' || t === 'theme_robotics' || t === 'theme_space' || t === 'theme_tesla'
        )
      ) {
        return 'theme';
      }
    }
    // Direkt-Liste
    if (DIVIDEND_ARISTOCRAT_SYMBOLS.has(sym) || input.is_dividend_aristocrat) return 'dividend';
    if (QUALITY_HINT_SYMBOLS.has(sym) || input.is_quality_value) return 'quality';
    // Default für Aktien ohne klare Zuordnung
    return 'momentum';
  }

  return null;
}

/**
 * V5.1 — Welche Strategien sind sinnvoll für diesen Asset-Subtype?
 * Wird von der Multi-Score-Engine genutzt um „nicht passende" Strategien zu skippen.
 */
export function applicableStrategiesForSubtype(subtype: AssetSubtype | null): Array<
  'breakout' | 'pullback' | 'relative_strength' | 'event' | 'longterm' | 'etf_rotation' | 'defensive' | 'crypto'
> {
  // Strategie-Konsolidierung (07.07., Strategien-Audit + User-OK):
  // 'relative_strength' ist in 'breakout' aufgegangen (beide bewerteten
  // dieselbe Zone am 52W-Hoch — RS war nur die Vor-Phase des Ausbruchs) und
  // 'event' (PEAD) ist STILLGELEGT (empirisch schwach, Datenlage oft lückig,
  // passt nicht zum antizyklischen Kernstil). Dateien + IDs bleiben erhalten
  // (Score-History/Weights referenzieren sie) — sie werden nur nirgends mehr
  // zugeordnet und können damit nicht mehr feuern.
  switch (subtype) {
    case 'tesla':
    case 'theme':
    case 'momentum':
      return ['breakout', 'pullback'];
    case 'quality':
      return ['pullback', 'longterm'];
    case 'dividend':
      // W91-Audit — 'pullback' ergänzt: Vorher konnten Aristokraten im
      // Bull-Markt strukturell nie über ~40 Punkte kommen (eine Strategie ×
      // longterm-Bull-Fit 40 = tote Zone) — selbst beim perfekten -25%-Dip.
      // Ein Einzelwert-Rücksetzer ist phasenunabhängig kaufbar (User-Stil);
      // pullback bringt eigene Bull-/Stage-Gates mit.
      return ['longterm', 'pullback'];
    case 'gold':
      return ['defensive'];
    case 'index_etf':
      return ['etf_rotation', 'pullback'];
    case 'sector_etf':
      // W94/F13 — defensive war toter Code: die Strategie prüft selbst
      // (defensive.ts) auf sector_etf (XLP/XLU-Rotation in risk_off),
      // wurde hier aber nie zugelassen. Zyklische Sektor-ETFs bekommen
      // von ihr schlicht einen niedrigen Score — eigenes Gate vorhanden.
      return ['etf_rotation', 'pullback', 'defensive'];
    case 'crypto_anchor':
      return ['crypto'];
    default:
      // Konsolidierung 07.07.: RS→breakout (eine Momentum-Option bleibt).
      return ['pullback', 'breakout'];
  }
}

/**
 * V5.1 — Position-Size-Multiplier basierend auf Asset-Subtype.
 * Tesla (High-Beta) bekommt halbierte Default-Größe.
 */
export function positionSizeMultiplierForSubtype(subtype: AssetSubtype | null): number {
  if (subtype === 'tesla') return 0.5;
  if (subtype === 'gold') return 0.75; // Edelmetalle typisch kleiner gewichten
  if (subtype === 'crypto_anchor') return 0.5; // Krypto höhere Volatilität
  return 1.0;
}
